if (!customElements.get('kakavy-personalization')) {
  class KakavyPersonalization extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.productFormId);
      this.textToggle = this.querySelector('[data-personalization-toggle]');
      this.logoToggle = this.querySelector('[data-logo-toggle]');
      this.fields = this.querySelector('[data-personalization-fields]');
      this.input = this.querySelector('[data-personalization-input]');
      this.counter = this.querySelector('[data-personalization-counter]');
      this.error = this.querySelector('[data-personalization-error]');
      this.buyNowButton = this.querySelector('[data-personalization-buy-now]');
      this.maxCharacters = Number(this.dataset.maxCharacters || 10);
      this.addonVariantId = Number(this.dataset.addonVariantId || 0);
      this.logoAddonVariantId = Number(this.dataset.logoAddonVariantId || 0);
      this.addonAvailable = this.dataset.addonAvailable === 'true';
      this.logoAddonAvailable = this.dataset.logoAddonAvailable === 'true';

      if (!this.form || (!this.textToggle && !this.logoToggle)) return;

      const productInfo = this.closest('product-info');
      this.stickyBar = document.querySelector('.sticky-atc-bar');
      this.acceleratedCheckouts = Array.from(
        new Set([
          ...(productInfo?.querySelectorAll('.shopify-payment-button') || []),
          ...(this.stickyBar?.querySelectorAll('.shopify-payment-button') || []),
        ])
      );
      const purchaseButtons = this.form.querySelector('.product-form__buttons');
      if (this.buyNowButton && purchaseButtons) {
        if (this.acceleratedCheckouts[0]) {
          this.acceleratedCheckouts[0].insertAdjacentElement('afterend', this.buyNowButton);
        } else {
          purchaseButtons.append(this.buyNowButton);
        }
      }

      if (this.textToggle) {
        this.textToggle.disabled = !this.addonAvailable || !this.addonVariantId;
        this.textToggle.addEventListener('change', this.handleTextToggle);
      }
      if (this.logoToggle) {
        this.logoToggle.disabled = !this.logoAddonAvailable || !this.logoAddonVariantId;
        this.logoToggle.addEventListener('change', this.handleLogoToggle);
      }
      this.input?.addEventListener('input', this.handleInput);
      this.buyNowButton?.addEventListener('click', this.handleBuyNow);
      document.addEventListener('click', this.handleProductSubmitClick, true);
      document.addEventListener('submit', this.handleSubmit, true);
      this.setAcceleratedCheckoutState(false);
    }

    disconnectedCallback() {
      this.textToggle?.removeEventListener('change', this.handleTextToggle);
      this.logoToggle?.removeEventListener('change', this.handleLogoToggle);
      this.input?.removeEventListener('input', this.handleInput);
      this.buyNowButton?.removeEventListener('click', this.handleBuyNow);
      document.removeEventListener('click', this.handleProductSubmitClick, true);
      document.removeEventListener('submit', this.handleSubmit, true);
    }

    handleTextToggle = () => {
      const active = this.textToggle.checked;
      this.textToggle.setAttribute('aria-expanded', String(active));
      if (this.fields) this.fields.hidden = !active;
      if (this.input) {
        this.input.disabled = !active;
        this.input.required = active;
      }
      this.setAcceleratedCheckoutState(this.hasActiveAddon());
      if (active) this.input?.focus();
      else this.clearError();
    };

    handleLogoToggle = () => {
      this.setAcceleratedCheckoutState(this.hasActiveAddon());
      this.clearError();
    };

    handleInput = () => {
      if (this.counter) this.counter.textContent = `${this.input.value.length}/${this.maxCharacters}`;
      this.clearError();
    };

    hasActiveAddon() {
      return Boolean(this.textToggle?.checked || this.logoToggle?.checked);
    }

    isValidText(value) {
      const allowed = new RegExp(`^[A-Za-z0-9 ]{1,${this.maxCharacters}}$`);
      if (value.trim().length === 0 || !allowed.test(value)) return false;
      const characterCounts = new Map();
      for (const character of value.toLowerCase()) {
        const count = (characterCounts.get(character) || 0) + 1;
        if (count > 2) return false;
        characterCounts.set(character, count);
      }
      return true;
    }

    showError(message) {
      if (!this.input || !this.error) return;
      this.input.setAttribute('aria-invalid', 'true');
      this.error.textContent = message;
      this.error.hidden = false;
      this.input.focus();
    }

    clearError() {
      this.input?.removeAttribute('aria-invalid');
      if (this.error) {
        this.error.hidden = true;
        this.error.textContent = '';
      }
    }

    getShopifyRoute(path) {
      const root = window.Shopify?.routes?.root || '/';
      return `${root}${path}`;
    }

    async parseJsonResponse(response, fallbackMessage) {
      const body = await response.text();
      let parsed;
      try { parsed = JSON.parse(body); }
      catch { throw new Error(fallbackMessage); }
      if (!response.ok || parsed.status) {
        throw new Error(parsed.description || parsed.message || fallbackMessage);
      }
      return parsed;
    }

    setAcceleratedCheckoutState(active) {
      for (const acceleratedCheckout of this.acceleratedCheckouts) acceleratedCheckout.hidden = active;
      if (this.buyNowButton) this.buyNowButton.hidden = !active;
      this.stickyBar?.classList.toggle('kakavy-personalization-hidden', active);
    }

    handleSubmit = (event) => {
      if (!this.hasActiveAddon() || !this.isCurrentProductForm(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addCustomizedItems(event.submitter || event.target.querySelector('[type="submit"]'), false, event.target);
    };

    handleProductSubmitClick = (event) => {
      if (!this.hasActiveAddon()) return;
      const submitButton = event.target.closest('button[type="submit"], input[type="submit"]');
      const submittedForm = submitButton?.form;
      if (!submitButton || !this.isCurrentProductForm(submittedForm)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addCustomizedItems(submitButton, false, submittedForm);
    };

    isCurrentProductForm(form) {
      if (!(form instanceof HTMLFormElement)) return false;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return false;
      const submittedVariantId = Number(new FormData(form).get('id'));
      const currentVariantId = Number(new FormData(this.form).get('id'));
      return submittedVariantId > 0 && submittedVariantId === currentVariantId;
    }

    handleBuyNow = (event) => {
      event.preventDefault();
      if (!this.hasActiveAddon()) return;
      this.addCustomizedItems(event.currentTarget, true, this.form);
    };

    addCustomizedItems = async (submitButton, checkoutNow, submittedForm = this.form) => {
      const textActive = Boolean(this.textToggle?.checked);
      const logoActive = Boolean(this.logoToggle?.checked);
      const text = this.input?.value || '';
      if (textActive && !this.isValidText(text)) {
        this.showError(this.dataset.errorText);
        return;
      }
      if (!textActive && !logoActive) return;
      if (submitButton?.disabled || submitButton?.getAttribute('aria-disabled') === 'true') return;

      this.clearError();
      submitButton?.setAttribute('aria-disabled', 'true');
      submitButton?.classList.add('btn--loading');
      let itemsAdded = false;

      try {
        const formData = new FormData(submittedForm);
        const mainVariantId = Number(formData.get('id'));
        const quantity = Math.max(1, Number(formData.get('quantity') || 1));
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value !== '') properties[match[1]] = value;
        }

        const pairId = `KPV-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        if (textActive) {
          properties[this.dataset.propertyName] = text;
          properties.Personalization = this.dataset.method;
        }
        if (logoActive) properties[this.dataset.logoPropertyName] = 'Yes';
        properties['_Kakavy personalization ID'] = pairId;

        const items = [{ id: mainVariantId, quantity, properties }];
        if (logoActive) {
          items.push({
            id: this.logoAddonVariantId,
            quantity,
            parent_id: mainVariantId,
            properties: {
              [this.dataset.logoPropertyName]: 'Yes',
              For: this.dataset.productTitle,
              '_Kakavy personalization ID': pairId,
              '_Product handle': this.dataset.productHandle,
            },
          });
        }
        if (textActive) {
          items.push({
            id: this.addonVariantId,
            quantity,
            parent_id: mainVariantId,
            properties: {
              'Custom Text': text,
              For: this.dataset.productTitle,
              '_Kakavy personalization ID': pairId,
              '_Product handle': this.dataset.productHandle,
            },
          });
        }

        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );
        const response = await fetch(this.getShopifyRoute('cart/add.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        const parsedState = await this.parseJsonResponse(
          response,
          'Unable to add customization. Please refresh the page and try again.'
        );
        itemsAdded = true;

        if (checkoutNow) {
          window.location.assign(this.getShopifyRoute('checkout'));
          return;
        }

        const cartResponse = await fetch(this.getShopifyRoute('cart.js'), {
          method: 'GET',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        const cart = await this.parseJsonResponse(cartResponse, 'Your items were added, but the cart could not refresh.');
        cart.sections = parsedState.sections;
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) cartDrawer.show(submitButton);
      } catch (error) {
        if (itemsAdded) {
          window.location.assign(this.getShopifyRoute('cart'));
          return;
        }
        this.showError(error.message || 'Unable to add customization.');
        const formError = this.form.querySelector('.product-form__error-message');
        if (formError) {
          formError.textContent = error.message;
          formError.hidden = false;
          formError.closest('.product-form__error-message-wrapper')?.removeAttribute('hidden');
        }
      } finally {
        submitButton?.classList.remove('btn--loading');
        submitButton?.removeAttribute('aria-disabled');
      }
    };
  }

  customElements.define('kakavy-personalization', KakavyPersonalization);
}
